<template>
  <div class="legal-policies settings fixed-heading">
    <div class="heading" style="display: flex; flex-direction: row; justify-content: space-between">
      <div>
        <h1>Legal Policies</h1>
        <p class="guide_text">Manage versioned legal policies and announcements for your ecosystem.</p>
      </div>
    </div>

    <div class="has-padding">
      <!-- Tab navigation -->
      <div class="tw-flex gap-0 mb-4 border-b">
        <button
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === 'documents' }"
          @click="activeTab = 'documents'"
        >
          Policy Documents
        </button>
        <button
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === 'announcements' }"
          @click="activeTab = 'announcements'"
        >
          Announcements
        </button>
      </div>

      <!-- Draft announcement banner on Policy Documents tab -->
      <div v-if="activeTab === 'documents' && draftAnnouncement" class="announcement-banner mb-4">
        <div class="tw-flex items-center justify-between">
          <div>
            <strong>Announcement draft</strong> —
            {{ draftAnnouncement.linked_policies_count || draftAnnouncement.linkedPolicies?.length || 0 }} linked document(s)
            · {{ draftAnnouncement.requiresAcceptance ? 'Acceptance required' : 'Informational' }}
            · {{ formatDate(draftAnnouncement.scheduledDate) }}
          </div>
          <ds-button
            label="View announcement"
            size="extra-small"
            variant="rounded-minimal"
            @click="activeTab = 'announcements'"
          />
        </div>
      </div>

      <!-- Policy Documents tab content -->
      <template v-if="activeTab === 'documents'">
        <div class="tw-flex items-center justify-between mb-4">
          <div class="tw-flex items-center gap-3">
            <dropdown
              class="min-w-[150px]"
              :options="filterOptions"
              :model-value="filterType"
              @update:model-value="filterType = $event"
              placeholder="All types"
            />
          </div>
          <ds-button
            label="+ Add policy version"
            size="extra-small"
            variant="rounded"
            @click="openCreateModal"
          />
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="text-center py-8">
          <loading/>
        </div>

        <!-- Empty state -->
        <div v-else-if="policies.length === 0" class="text-center py-16">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">No policy documents yet</h3>
          <p class="text-gray-500 mb-4">Create your first policy version to get started.</p>
          <ds-button label="+ Add policy version" @click="openCreateModal" variant="rounded"/>
        </div>

        <!-- Document register table -->
        <table v-else class="table">
          <thead>
            <tr>
              <th>Policy Document</th>
              <th>Version</th>
              <th>Status</th>
              <th>Effective Date</th>
              <th>Format</th>
              <th>Acceptance</th>
              <th>Last Updated By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="policy in policies" :key="policy.id">
              <td>{{ typeLabel(policy.type) }}</td>
              <td>{{ policy.version_name }}</td>
              <td>
                <span class="status" :class="'status--' + policy.display_status">
                  {{ statusLabel(policy.display_status) }}
                </span>
              </td>
              <td>{{ policy.active_from || '—' }}</td>
              <td>{{ policy.format }}</td>
              <td>{{ policy.display_status === 'pending' ? '—' : `${policy.accepted_count} / ${policy.total_users}` }}</td>
              <td>{{ policy.last_updated_by ? policy.last_updated_by.name : '—' }}</td>
              <td>
                <div class="tw-flex gap-1">
                  <ds-button
                    v-if="policy.has_markdown || policy.link"
                    label="View"
                    variant="rounded-minimal"
                    size="extra-small"
                    @click="viewPolicy(policy)"
                  />
                  <ds-button
                    v-if="policy.has_markdown || policy.document"
                    label="Download"
                    variant="rounded-minimal"
                    size="extra-small"
                    @click="downloadPolicy(policy)"
                  />
                  <ds-button
                    v-if="policy.display_status === 'pending'"
                    label="Edit"
                    variant="rounded-minimal"
                    size="extra-small"
                    @click="openEditModal(policy)"
                  />
                  <ds-button
                    v-if="policy.display_status === 'pending'"
                    label="Delete"
                    variant="rounded-minimal"
                    size="extra-small"
                    style="color: red;"
                    @click="deletePolicy(policy)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <!-- Announcements tab content (placeholder — will be implemented in Phase 4) -->
      <template v-if="activeTab === 'announcements'">
        <div class="tw-flex items-center justify-between mb-4">
          <div></div>
          <ds-button
            label="Create announcement"
            size="extra-small"
            variant="rounded"
            @click="openAnnouncementForm"
            :disabled="!!draftAnnouncement"
          />
        </div>

        <div v-if="loadingAnnouncements" class="text-center py-8">
          <loading/>
        </div>

        <div v-else-if="announcements.length === 0" class="text-center py-16">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">No announcements yet</h3>
          <p class="text-gray-500 mb-4">Create an announcement to notify users about policy changes.</p>
          <ds-button label="Create announcement" @click="openAnnouncementForm" variant="rounded"/>
        </div>

        <table v-else class="table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Effective Date</th>
              <th>Documents</th>
              <th>Acceptance</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="announcement in announcements" :key="announcement.id">
              <td>
                <span class="status" :class="'status--' + announcement.status">
                  {{ announcement.status === 'draft' ? 'Draft' : 'Published' }}
                </span>
              </td>
              <td>{{ formatDate(announcement.scheduledDate) }}</td>
              <td>
                <span v-for="(lp, i) in announcement.linkedPolicies" :key="lp.id">
                  {{ typeLabel(lp.type) }} {{ lp.version_name }}<span v-if="i < announcement.linkedPolicies.length - 1">, </span>
                </span>
              </td>
              <td>{{ announcement.requiresAcceptance ? 'Required' : 'Informational' }}</td>
              <td>
                <div class="tw-flex gap-1">
                  <ds-button
                    v-if="announcement.status === 'draft'"
                    :label="$t('legal_announcement_preview')"
                    variant="rounded-minimal"
                    size="extra-small"
                    @click="previewDraftAnnouncement(announcement)"
                  />
                  <ds-button
                    v-if="announcement.status === 'draft'"
                    label="Edit"
                    variant="rounded-minimal"
                    size="extra-small"
                    @click="editAnnouncement(announcement)"
                  />
                  <ds-button
                    v-if="announcement.status === 'draft'"
                    label="Delete"
                    variant="rounded-minimal"
                    size="extra-small"
                    style="color: red;"
                    @click="deleteAnnouncement(announcement)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <!-- Policy version modal -->
      <policy-version-modal
        v-if="showVersionModal"
        :policy="editingPolicy"
        @save="handleVersionSave"
        @cancel="showVersionModal = false"
      />

      <!-- Announcement form modal -->
      <announcement-form
        v-if="showAnnouncementForm"
        :announcement="editingAnnouncement"
        :pending-policies="pendingPolicies"
        @save="handleAnnouncementSave"
        @cancel="showAnnouncementForm = false"
      />

      <!-- Announcement preview modal -->
      <policy-announcement-modal
        v-if="previewAnnouncement"
        :announcement="previewAnnouncement"
        :preview="true"
        @accepted="previewAnnouncement = null"
      />
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import Loading from '@/components/Dashboard/ConceptMap/Loading.vue'
  import PolicyVersionModal from '@/components/Settings/PolicyVersionModal.vue'
  import AnnouncementForm from '@/components/Settings/AnnouncementForm.vue'
  import PolicyAnnouncementModal from '@/components/Modals/PolicyAnnouncementModal.vue'
  import * as LegalPoliciesApi from '@/Domain/Policy/Api/legal-policies'

  const TYPE_LABELS = {
    'terms-of-use': 'Terms of Use',
    'privacy-policy': 'Privacy Policy',
    'cookie-policy': 'Cookie Policy',
  }

  const FILTER_OPTIONS = [
    { value: '', label: 'All types' },
    { value: 'terms-of-use', label: 'Terms of Use' },
    { value: 'privacy-policy', label: 'Privacy Policy' },
    { value: 'cookie-policy', label: 'Cookie Policy' },
  ]

  export default defineComponent({
    name: 'LegalPolicies',
    components: { DsButton, Dropdown, Icon, Loading, PolicyVersionModal, AnnouncementForm, PolicyAnnouncementModal },
    data() {
      return {
        activeTab: 'documents',
        policies: [],
        announcements: [],
        draftAnnouncement: null,
        loading: true,
        loadingAnnouncements: false,
        filterType: '',
        showVersionModal: false,
        editingPolicy: null,
        showAnnouncementForm: false,
        editingAnnouncement: null,
        previewAnnouncement: null,
      }
    },
    computed: {
      filterOptions() {
        return FILTER_OPTIONS
      },
      pendingPolicies() {
        return this.policies.filter(p => p.display_status === 'pending')
      },
    },
    methods: {
      typeLabel(type) {
        return TYPE_LABELS[type] || type
      },
      statusLabel(status) {
        if (status === 'pending') return 'Pending publication'
        if (status === 'active') return 'Active'
        if (status === 'superseded') return 'Superseded'
        return status
      },
      formatDate(dateStr) {
        if (!dateStr) return '—'
        const d = new Date(dateStr)
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      },
      async fetchPolicies() {
        this.loading = true
        try {
          const type = this.filterType || undefined
          const response = await LegalPoliciesApi.fetchAll(type)
          this.policies = response.data || []
        } catch (e) {
          console.error('Failed to load legal policies', e)
          this.policies = []
        }
        this.loading = false
      },
      async fetchDraftAnnouncement() {
        try {
          const { fetchDraft } = await import('@/Domain/Policy/Api/announcements')
          const response = await fetchDraft()
          this.draftAnnouncement = response.data || null
        } catch (e) {
          this.draftAnnouncement = null
        }
      },
      async fetchAnnouncements() {
        this.loadingAnnouncements = true
        try {
          const { fetchAll } = await import('@/Domain/Policy/Api/announcements')
          const response = await fetchAll()
          this.announcements = response.data || []
        } catch (e) {
          console.error('Failed to load announcements', e)
          this.announcements = []
        }
        this.loadingAnnouncements = false
      },
      openCreateModal() {
        this.editingPolicy = null
        this.showVersionModal = true
      },
      openEditModal(policy) {
        this.editingPolicy = policy
        this.showVersionModal = true
      },
      async handleVersionSave() {
        this.showVersionModal = false
        await this.fetchPolicies()
      },
      viewPolicy(policy) {
        if (policy.link) {
          window.open(policy.link, '_blank')
        } else {
          window.open(`/${policy.type}/${policy.id}`, '_blank')
        }
      },
      downloadPolicy(policy) {
        const url = LegalPoliciesApi.downloadUrl(policy.id, policy.type)
        window.open(url, '_blank')
      },
      async deletePolicy(policy) {
        if (!confirm(`Delete draft "${this.typeLabel(policy.type)} ${policy.version_name}"?`)) {
          return
        }

        try {
          await LegalPoliciesApi.remove(policy.id)
          await this.fetchPolicies()
        } catch (e) {
          console.error('Failed to delete policy', e)
          alert('Failed to delete policy version.')
        }
      },
      openAnnouncementForm() {
        this.editingAnnouncement = null
        this.showAnnouncementForm = true
      },
      editAnnouncement(announcement) {
        this.editingAnnouncement = announcement
        this.showAnnouncementForm = true
      },
      previewDraftAnnouncement(announcement) {
        this.previewAnnouncement = {
          id: announcement.id,
          message: announcement.message,
          requires_acceptance: announcement.requiresAcceptance,
          linked_policies: (announcement.linkedPolicies || []).map(p => ({
            type: p.type,
            version_name: p.versionName || p.version_name,
            url: '/' + p.type + '/' + p.id,
            active_from: announcement.scheduledDate,
          })),
        }
      },
      async handleAnnouncementSave() {
        this.showAnnouncementForm = false
        this.editingAnnouncement = null
        await Promise.all([
          this.fetchAnnouncements(),
          this.fetchDraftAnnouncement(),
        ])
      },
      async deleteAnnouncement(announcement) {
        if (!confirm('Delete this draft announcement?')) return

        try {
          const { remove } = await import('@/Domain/Policy/Api/announcements')
          await remove(announcement.id)
          await Promise.all([
            this.fetchAnnouncements(),
            this.fetchDraftAnnouncement(),
          ])
        } catch (e) {
          console.error('Failed to delete announcement', e)
          alert('Failed to delete announcement.')
        }
      },
    },
    watch: {
      filterType() {
        this.fetchPolicies()
      },
      activeTab(tab) {
        if (tab === 'announcements' && this.announcements.length === 0) {
          this.fetchAnnouncements()
        }
      },
    },
    mounted() {
      this.fetchPolicies()
      this.fetchDraftAnnouncement()
    },
  })
</script>

<style scoped>
  .tab-btn {
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    cursor: pointer;
  }

  .tab-btn--active {
    color: #333;
    border-bottom-color: #333;
  }

  .tab-btn:hover {
    color: #333;
  }

  .announcement-banner {
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-radius: 6px;
    padding: 12px 16px;
    font-size: 14px;
  }

  .status {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
  }

  .status--active {
    background: #e6f9ee;
    color: #0d7a3e;
  }

  .status--pending {
    background: #f0f0f0;
    color: #555;
  }

  .status--draft {
    background: #fff3e0;
    color: #e65100;
  }

  .status--published {
    background: #e3f2fd;
    color: #1565c0;
  }

  .status--superseded {
    background: #f5f5f5;
    color: #999;
  }
</style>
